import {
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { UserSettings, Option } from 'models';
import { useDispatch } from 'react-redux';
import { serviceDateFormat } from 'utils';
import { actions as PersonInformationActions } from 'store/person-information/slicer';
import {
  validateBankAccounts,
  collectData,
  compareSettings,
  Label,
  CollectedData,
} from './helpers';
import { format } from 'date-fns';
import { Location } from 'history';
import services, { type ServiceError } from 'services';
import {
  map as _map,
  concat as _concat,
  filter as _filter,
  set as _set,
  omit as _omit,
} from 'lodash';

import { PersonInformationDetails } from 'components';
import { useStateSelector } from 'hooks';

import isServerValidationError from './isServerValidationError';

const stringToBoolean = (str: string) => str === 'true';

export interface ComponentState {
  loading: boolean;
  error: boolean;
  countries: string[];
  openAlternative: boolean;
  useAlternativeEmail: boolean;
  useAlternativeAddress: boolean;
  useMailForRealise: boolean;
  useAddressForRealise: boolean;
  useMailForMeeting: boolean;
  useAddressForMeeting: boolean;
  upload: boolean;
  saveConfirm: boolean;
  hasSSN: boolean;
}

const PersonInformation = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const prevUserSettings = useRef<CollectedData | null>(null);
  const { saved, queue } = useStateSelector((state) => state.personInformation);
  const [state, setState] = useState<ComponentState>({
    loading: true,
    error: false,
    openAlternative: false,
    useAlternativeEmail: false,
    useAlternativeAddress: false,
    useMailForRealise: false,
    useMailForMeeting: false,
    useAddressForRealise: true,
    useAddressForMeeting: true,
    countries: [],
    upload: false,
    saveConfirm: false,
    hasSSN: false,
  });

  const formik = useFormik<
    Omit<UserSettings, 'HasSocialSecurityNumber'> & {
      SocialSecurityNumber: string | null;
    }
  >({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    initialValues: {
      Address: null,
      AllowNotifications: null,
      AlternativeInvoiceAddress: null,
      AlternativeInvoiceEmail: null,
      AlternativeMailAddress: null,
      AlternativeMailEmail: null,
      BankAccounts: [],
      Birthday: null,
      CanEditUserSettings: null,
      Email: null,
      FirstName: null,
      LastName: null,
      FullName: null,
      Id: null,
      InvoiceChannelPreferred: null,
      MarketingPermission: null,
      Phone: null,
      SocialSecurityNumber: null,
      Tax: null,
      UseAddressForMeeting: null,
      UseAddressForRelease: null,
      UseAlternativeMailAddressForMeeting: null,
      UseAlternativeMailAddressForRelease: null,
      UseAlternativeMailEmailForMeeting: null,
      UseAlternativeMailEmailForRelease: null,
      UseEmailForMeeting: null,
      UseEmailForRelease: null,
    },
    validate: (values) => {
      let errors = {};
      const socialSecurityNumberPattern = new RegExp(
        /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])([0-9]{2})(A|\-)([0-9]{3}[A-Y0-9]{1})$/
      );
      const phonePattern = new RegExp(/^\+[1-9]{1}[0-9]{6,14}$/);

      if (values.SocialSecurityNumber) {
        if (!socialSecurityNumberPattern.test(values.SocialSecurityNumber)) {
          _set(errors, 'SocialSecurityNumber', Label.Invalid);
          return errors;
        }
      }

      if (!values.Birthday) {
        _set(errors, 'Birthday', Label.Required);
        return errors;
      }

      if (!values.Email?.Email) {
        _set(errors, 'EmailEmail', Label.Required);
        return errors;
      }

      if (!values.Phone?.Phone) {
        _set(errors, 'PhonePhone', Label.Required);
        return errors;
      }

      if (values.Phone?.Phone) {
        if (!phonePattern.test(values.Phone.Phone)) {
          _set(errors, 'PhonePhone', Label.Invalid);
          return errors;
        }
      }

      if (!values.Address?.Street1) {
        _set(errors, 'AddressStreet1', Label.Required);
        return errors;
      }

      if (!values.Address?.PostalCode) {
        _set(errors, 'AddressPostalCode', Label.Required);
        return errors;
      }

      if (!values.Address?.PostalArea) {
        _set(errors, 'AddressPostalArea', Label.Required);
        return errors;
      }

      if (!values.Address?.Country) {
        _set(errors, 'AddressCountry', Label.Required);
        return errors;
      }

      if (state.openAlternative) {
        if (state.useAlternativeEmail) {
          if (!values.AlternativeMailEmail?.Email) {
            _set(errors, 'AlternativeMailEmailEmail', Label.Required);
            return errors;
          }
        }

        if (state.useAlternativeAddress) {
          if (!values.AlternativeMailAddress?.Street1) {
            _set(errors, 'AlternativeMailAddressStreet1', Label.Required);
            return errors;
          }

          if (!values.AlternativeMailAddress?.Country) {
            _set(errors, 'AlternativeMailAddressCountry', Label.Required);
            return errors;
          }

          if (!values.AlternativeMailAddress?.PostalCode) {
            _set(errors, 'AlternativeMailAddressPostalCode', Label.Required);
            return errors;
          }

          if (!values.AlternativeMailAddress?.PostalArea) {
            _set(errors, 'AlternativeMailAddressPostalArea', Label.Required);
            return errors;
          }
        }
      }

      if (values.BankAccounts.length) {
        return validateBankAccounts(values.BankAccounts);
      }

      return errors;
    },
    onSubmit: async (values) => {
      let body: Partial<{ [key: string]: any }> = {
        Address: values.Address,
        AlternativeInvoiceAddress: values.AlternativeInvoiceAddress,
        AlternativeInvoiceEmail: values.AlternativeInvoiceEmail,
        AlternativeMailAddress: values.AlternativeMailAddress,
        AlternativeMailEmail: values.AlternativeMailEmail,
        BankAccounts: _map(values.BankAccounts, (BankAccount) => ({
          ...BankAccount,
          Id: Number.isNaN(+BankAccount.Id) ? BankAccount.Id : null,
        })),
        Birthday: values.Birthday,
        Email: values.Email,
        Id: values.Id,
        InvoiceChannelPreferred: values.InvoiceChannelPreferred,
        MarketingPermission: values.MarketingPermission,
        Phone: values.Phone,
        Tax: values.Tax,
        SocialSecurityNumber: values.SocialSecurityNumber,
        SaveSocialSecurityNumber: true,
        UseAddressForMeeting: values.UseAddressForMeeting,
        UseAddressForRelease: values.UseAddressForRelease,
        UseAlternativeMailAddressForMeeting:
          values.UseAlternativeMailAddressForMeeting,
        UseAlternativeMailAddressForRelease:
          values.UseAlternativeMailAddressForRelease,
        UseAlternativeMailEmailForMeeting:
          values.UseAlternativeMailEmailForMeeting,
        UseAlternativeMailEmailForRelease:
          values.UseAlternativeMailEmailForRelease,
        UseEmailForMeeting: values.UseEmailForMeeting,
        UseEmailForRelease: values.UseEmailForRelease,
      };

      if (state.useMailForMeeting) {
        body.UseEmailForMeeting = !state.useAlternativeEmail;
        body.UseAlternativeMailEmailForMeeting = state.useAlternativeEmail;

        body.UseAddressForMeeting = false;
        body.UseAlternativeMailAddressForMeeting = false;
      }

      if (state.useAddressForMeeting) {
        body.UseAddressForMeeting = !state.useAlternativeAddress;
        body.UseAlternativeMailAddressForMeeting = state.useAlternativeAddress;

        body.UseEmailForMeeting = false;
        body.UseAlternativeMailEmailForMeeting = false;
      }

      if (state.useMailForRealise) {
        body.UseEmailForRelease = !state.useAlternativeEmail;
        body.UseAlternativeMailEmailForRelease = state.useAlternativeEmail;

        body.UseAddressForRelease = false;
        body.UseAlternativeMailAddressForRelease = false;
      }

      if (state.useAddressForRealise) {
        body.UseAddressForRelease = !state.useAlternativeAddress;
        body.UseAlternativeMailAddressForRelease = state.useAlternativeAddress;

        body.UseEmailForRelease = false;
        body.UseAlternativeMailEmailForRelease = false;
      }

      if (state.hasSSN) {
        if (!values.SocialSecurityNumber) {
          body = _omit(body, [
            'SocialSecurityNumber',
            'SaveSocialSecurityNumber',
          ]);
        }
      }

      setState((prevState) => ({ ...prevState, upload: true }));

      try {
        const userSettings: UserSettings = (
          await services.post('/api/user/settings', body)
        ).data.Data;

        const openAlternative = Boolean(
          userSettings.UseAlternativeMailEmailForRelease ||
            userSettings.UseAlternativeMailEmailForMeeting ||
            userSettings.UseAlternativeMailAddressForRelease ||
            userSettings.UseAlternativeMailAddressForMeeting
        );
        const useAlternativeEmail = Boolean(
          userSettings.UseAlternativeMailEmailForRelease ||
            userSettings.UseAlternativeMailEmailForMeeting
        );
        const useAlternativeAddress = Boolean(
          userSettings.UseAlternativeMailAddressForRelease ||
            userSettings.UseAlternativeMailAddressForMeeting
        );
        const hasSSN = Boolean(userSettings.HasSocialSecurityNumber);
        const useMailForRealise = Boolean(
          userSettings.UseEmailForRelease ||
            userSettings.UseAlternativeMailEmailForRelease
        );
        const useMailForMeeting = Boolean(
          userSettings.UseEmailForMeeting ||
            userSettings.UseAlternativeMailEmailForMeeting
        );

        prevUserSettings.current = collectData(userSettings, {
          ...state,
          openAlternative,
          useAlternativeEmail,
          useAlternativeAddress,
          useMailForMeeting,
          useMailForRealise,
          useAddressForMeeting: !useMailForMeeting,
          useAddressForRealise: !useMailForRealise,
          hasSSN,
        });
        formik.setValues({ ...userSettings, SocialSecurityNumber: '' });

        setState((prevState) => ({
          ...prevState,
          openAlternative,
          useAlternativeEmail,
          useAlternativeAddress,
          useMailForMeeting,
          useMailForRealise,
          useAddressForMeeting: !useMailForMeeting,
          useAddressForRealise: !useMailForRealise,
          hasSSN,
          upload: false,
        }));
      } catch (err) {
        if (isServerValidationError(err)) {
          Object.keys(err.response?.data.ModelState).forEach((errorKey) => {
            if (errorKey === 'model.Email.Email') {
              formik.setFieldError('EmailEmail', Label.Invalid);
            }

            if (errorKey === 'model.AlternativeMailEmail.Email') {
              formik.setFieldError('AlternativeMailEmailEmail', Label.Invalid);
            }
          });
        }
        console.error(err);
        setState((prevState) => ({ ...prevState, upload: false }));
      }
    },
  });

  useEffect(() => {
    fetchUserSettings();

    /* eslint-disable-next-line */
  }, []);

  const fetchUserSettings = async () => {
    try {
      setState((prevState) => ({ ...prevState, loading: true, error: false }));

      const userSettings: UserSettings = (
        await services.get('/api/user/settings')
      ).data.Data;

      const countries: Option[] = (
        await services.get('/api/user/settings/form/update')
      ).data?.Data?.Countries;

      const openAlternative = Boolean(
        userSettings.UseAlternativeMailEmailForRelease ||
          userSettings.UseAlternativeMailEmailForMeeting ||
          userSettings.UseAlternativeMailAddressForRelease ||
          userSettings.UseAlternativeMailAddressForMeeting
      );
      const useAlternativeEmail = Boolean(
        userSettings.UseAlternativeMailEmailForRelease ||
          userSettings.UseAlternativeMailEmailForMeeting
      );
      const useAlternativeAddress = Boolean(
        userSettings.UseAlternativeMailAddressForRelease ||
          userSettings.UseAlternativeMailAddressForMeeting
      );
      const useMailForRealise = Boolean(
        userSettings.UseEmailForRelease ||
          userSettings.UseAlternativeMailEmailForRelease
      );
      const useMailForMeeting = Boolean(
        userSettings.UseEmailForMeeting ||
          userSettings.UseAlternativeMailEmailForMeeting
      );
      const hasSSN = Boolean(userSettings.HasSocialSecurityNumber);

      prevUserSettings.current = collectData(userSettings, {
        ...state,
        openAlternative,
        useAlternativeEmail,
        useAlternativeAddress,
        useMailForMeeting,
        useMailForRealise,
        useAddressForMeeting: !useMailForMeeting,
        useAddressForRealise: !useMailForRealise,
        hasSSN,
      });

      formik.setValues({ ...userSettings, SocialSecurityNumber: '' });

      setState((prevState) => ({
        ...prevState,
        loading: false,
        openAlternative,
        useAlternativeEmail,
        useAlternativeAddress,
        useMailForMeeting,
        useMailForRealise,
        useAddressForMeeting: !useMailForMeeting,
        useAddressForRealise: !useMailForRealise,
        hasSSN,
        countries: countries
          ? _map(countries, (country) => country.Label || '')
          : [],
      }));
    } catch (err) {
      console.error(err);
      setState((prevState) => ({ ...prevState, loading: false, error: true }));
    }
  };

  const handleChangeInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      formik.setFieldValue(name, value);
    },
    [formik]
  );

  const handleChangeCountry = useCallback(
    (e: ChangeEvent<unknown>, country: string | null) => {
      formik.setFieldValue('Address.Country', country);
    },
    [formik]
  );

  const handleChangeAlternativeCountry = useCallback(
    (e: ChangeEvent<unknown>, country: string | null) => {
      formik.setFieldValue('AlternativeMailAddress.Country', country);
    },
    [formik]
  );

  const handleToggleCustomCheckbox = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked, name } = e.target;

      setState((prevState) => ({
        ...prevState,
        [name]: checked,
      }));
    },
    []
  );

  const handleToggleCheckbox = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked, name } = e.target;

      formik.setFieldValue(name, checked);
    },
    [formik]
  );

  const handleToggleAlternativeView = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      setState((prevState) => ({
        ...prevState,
        openAlternative: checked,
        useAlternativeEmail: false,
        useAlternativeAddress: false,
      }));
    },
    []
  );

  const addBankAccount = useCallback(() => {
    formik.setFieldValue(
      'BankAccounts',
      _concat(formik.values.BankAccounts, {
        Id: `${Math.random()}`,
        IsCommission: false,
        IsReimbursement: false,
        Number: null,
      })
    );
  }, [formik]);

  const removeBankAccount = useCallback(
    (id: string) => {
      formik.setFieldValue(
        'BankAccounts',
        _filter(
          formik.values.BankAccounts,
          (BankAccount) => BankAccount.Id !== id
        )
      );
    },
    [formik]
  );

  const handleChangeTax = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let newValue = +e.target.value;

      if (typeof newValue !== 'number' || newValue < 0) {
        newValue = 0;
      }

      formik.setFieldValue('Tax', newValue);
    },
    [formik]
  );

  const handleChangeDate = useCallback(
    (date: Date | null, name?: string) => {
      if (name) {
        formik.setFieldValue(
          name,
          date ? format(date, serviceDateFormat) : null
        );
      }
    },
    [formik]
  );

  const handleChangeRadio = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target;

      if (name === 'useMailForMeeting') {
        setState((prevState) => ({
          ...prevState,
          useMailForMeeting: stringToBoolean(value),
          useAddressForMeeting: !stringToBoolean(value),
        }));
      }

      if (name === 'useMailForRealise') {
        setState((prevState) => ({
          ...prevState,
          useMailForRealise: stringToBoolean(value),
          useAddressForRealise: !stringToBoolean(value),
        }));
      }

      if (name === 'MarketingPermission') {
        formik.setValues({
          ...formik.values,
          MarketingPermission: stringToBoolean(value),
        });
      }
    },
    [formik]
  );

  const handleSubmitForm = useCallback(async () => {
    await formik.submitForm();
  }, [formik]);

  const equal = useMemo(() => {
    const newData = collectData(formik.values, state);
    if (prevUserSettings.current) {
      return compareSettings(newData, prevUserSettings.current);
    }

    return false;
  }, [formik.values, state]);

  useEffect(() => {
    dispatch(PersonInformationActions.setSavedFlag(equal));
  }, [equal]);

  const shouldBlockNavigation = useCallback(
    (location: Location) => !equal,
    [equal]
  );

  const clearQueue = () => {
    dispatch(PersonInformationActions.clearQueue());
  };

  const navigate = useCallback((path: string) => history.push(path), [history]);

  const handleInitErrors = useCallback(() => formik.setErrors({}), [formik]);

  return (
    <>
      <PersonInformationDetails
        {...state}
        queue={queue}
        saved={saved}
        errors={formik.errors}
        values={formik.values}
        equal={equal}
        navigate={navigate}
        shouldBlockNavigation={shouldBlockNavigation}
        handleChangeDate={handleChangeDate}
        handleChangeInput={handleChangeInput}
        handleChangeCountry={handleChangeCountry}
        handleChangeAlternativeCountry={handleChangeAlternativeCountry}
        handleToggleAlternativeView={handleToggleAlternativeView}
        handleToggleCustomCheckbox={handleToggleCustomCheckbox}
        handleToggleCheckbox={handleToggleCheckbox}
        addBankAccount={addBankAccount}
        removeBankAccount={removeBankAccount}
        handleChangeTax={handleChangeTax}
        handleChangeRadio={handleChangeRadio}
        handleSubmitForm={handleSubmitForm}
        handleInitErrors={handleInitErrors}
        clearQueue={clearQueue}
      />
    </>
  );
};

export default PersonInformation;
